import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Collapse,
    Divider,
    Drawer as MuiDrawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem as MuiMenuItem,
    Theme,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    Launch as ExternalLinkIcon,
    MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { IUser } from '../../types/User.interface';
import { helpURL } from '../../config';
import { LanguageLinks } from '../Buttons/LanguageSwitch';
import MenuItem, { MenuItemNode } from './MenuItem';
import { _links } from './_links';
import { findMenuItem } from './findMenuItem';

const DRAWER_WIDTH = 240;

const openedMixin = (theme: Theme) => ({
    width: DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(9)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    width: DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [],
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

interface SideDrawerProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    onSelectedMenuItemChange?: (menuItem: string) => void;
    user: IUser;
}

const SideDrawer: FC<SideDrawerProps> = ({ open, setOpen, onSelectedMenuItemChange, user }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const [openMenuItems, setOpenMenuItems] = useState<Record<string, boolean>>({});
    const [legalMenuAnchorEl, setLegalMenuAnchorEl] = useState<null | HTMLElement>(null);

    const legalLinks = useMemo(
        () => [
            { label: 'Impressum', path: '/impressum' },
            { label: 'Datenschutz', path: '/dataprivacy' },
        ],
        []
    );

    const openLegalMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setLegalMenuAnchorEl(event.currentTarget);
    }, []);

    const closeLegalMenu = useCallback(() => {
        setLegalMenuAnchorEl(null);
    }, []);

    const loggedInUserRoles = useMemo(() => user?.roles ?? [], [user?.roles]);

    const isItemAllowedForUser = useCallback(
        (item: MenuItemNode): boolean => {
            if (!user) return false;
            const { role, roles, permissions } = item;
            const hasPermissions =
                permissions && permissions.length
                    ? permissions.every((p) => (user.permissions || []).includes(p))
                    : true;
            const hasRole =
                roles && roles.length
                    ? roles.some((r) => loggedInUserRoles.includes(r))
                    : role
                      ? loggedInUserRoles.includes(role)
                      : true;
            if (!hasRole || !hasPermissions) return false;
            if ('nestedItems' in item && item.nestedItems.length > 0) {
                return item.nestedItems.some((child) => isItemAllowedForUser(child));
            }
            return true;
        },
        [loggedInUserRoles, user]
    );

    const menuItems = useMemo<MenuItemNode[]>(() => _links.filter(isItemAllowedForUser), [isItemAllowedForUser]);

    const handleDrawerClose = useCallback(() => {
        setOpen(false);
        setOpenMenuItems({});
    }, [setOpen, setOpenMenuItems]);

    const selectMenuItem = useCallback(
        (item: MenuItemNode) => {
            setOpen(true);
            'nestedItems' in item
                ? setOpenMenuItems((prevState) => ({
                      ...prevState,
                      [item.value]: !prevState[item.value],
                  }))
                : navigate(item.path || '');
        },
        [navigate, setOpen]
    );

    // The title is NOT set here: navigation can be refused (the Budget pages block it while edits are
    // pending, CALL-779), and a title set on click then outlived the page the user stayed on. The effect
    // below derives it from the path that actually won — which is how the top-level items already worked.
    const selectNestedMenuItem = useCallback(
        (nestedItem: MenuItemNode) => {
            setOpen(true);
            if ('path' in nestedItem) {
                navigate(nestedItem.path || '');
            }
        },
        [navigate, setOpen]
    );

    // Longest-prefix match, so detail routes (e.g. /budget/scenarios/5) resolve
    // to their closest menu entry; shared by the title/auto-expand effect and
    // the per-item selection highlight below.
    const [currentMenuItem, currentMenuItemParents] = useMemo(
        () => findMenuItem(menuItems, location.pathname),
        [menuItems, location]
    );
    const selectedMenuItemPath = currentMenuItem && 'path' in currentMenuItem ? currentMenuItem.path : undefined;

    const updateMenuState = useCallback(() => {
        if (currentMenuItem && onSelectedMenuItemChange) {
            onSelectedMenuItemChange(
                currentMenuItem.value ? t(currentMenuItem.value, { ns: 'navigation' }) : currentMenuItem.text || ''
            );
        }
        if (open && currentMenuItemParents?.length > 0) {
            setOpenMenuItems((prevState) => ({
                ...prevState,
                ...currentMenuItemParents.reduce((acc, item) => ({ ...acc, [item.value]: true }), {}),
            }));
        }
    }, [open, currentMenuItem, currentMenuItemParents, onSelectedMenuItemChange, setOpenMenuItems, t]);

    useEffect(() => {
        updateMenuState();
    }, [updateMenuState]);

    const showMenuItemExpandIcon = useCallback(
        (item: MenuItemNode) => {
            if ('nestedItems' in item && item.nestedItems.length > 0) {
                if (openMenuItems[item.value]) return <ExpandLessIcon />;

                return <ExpandMoreIcon />;
            }

            return null;
        },
        [openMenuItems]
    );

    return (
        <Drawer variant="permanent" open={open} anchor="left">
            <DrawerHeader>
                <IconButton onClick={handleDrawerClose}>
                    {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
            </DrawerHeader>

            <Divider />

            <List>
                {menuItems.map((item) => (
                    <Box key={item.value}>
                        <MenuItem
                            item={item}
                            isMenuOpen={open}
                            showMenuItemExpandIcon={showMenuItemExpandIcon}
                            onClick={() => selectMenuItem(item)}
                            selectedPath={selectedMenuItemPath}
                            isNestedItem={false}
                            nestedItems={'nestedItems' in item ? item.nestedItems : undefined}
                        ></MenuItem>
                        {'nestedItems' in item && (
                            <Collapse in={openMenuItems[item.value]} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {item.nestedItems.filter(isItemAllowedForUser).map((nestedItem) => (
                                        <MenuItem
                                            key={nestedItem.value}
                                            item={nestedItem}
                                            isNestedItem={true}
                                            isMenuOpen={open}
                                            showMenuItemExpandIcon={showMenuItemExpandIcon}
                                            onClick={() => selectNestedMenuItem(nestedItem)}
                                            selectedPath={selectedMenuItemPath}
                                        ></MenuItem>
                                    ))}
                                </List>
                            </Collapse>
                        )}
                    </Box>
                ))}
            </List>
            <Box sx={{ marginTop: { sm: 'auto' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {open && helpURL && (
                    <ListItemButton
                        component={'a'}
                        href={helpURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ flexGrow: 1 }}
                    >
                        <ListItemIcon>
                            <ExternalLinkIcon />
                        </ListItemIcon>
                        <ListItemText primary={t('nav.help')} />
                    </ListItemButton>
                )}
                <IconButton
                    onClick={openLegalMenu}
                    title={t('nav.legalInformation')}
                    aria-label={t('nav.legalInformation')}
                    aria-haspopup="true"
                    sx={{ mr: open ? 1 : 0 }}
                >
                    <MoreVertIcon />
                </IconButton>
                <Menu
                    anchorEl={legalMenuAnchorEl}
                    open={Boolean(legalMenuAnchorEl)}
                    onClose={closeLegalMenu}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                    {/* When collapsed, the Help row is hidden, so surface Help inside the menu */}
                    {!open && helpURL && (
                        <MuiMenuItem
                            component={'a'}
                            href={helpURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={closeLegalMenu}
                        >
                            <ListItemIcon>
                                <ExternalLinkIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{t('nav.help')}</ListItemText>
                        </MuiMenuItem>
                    )}
                    {!open && helpURL && <Divider />}
                    {legalLinks.map((link) => (
                        <MuiMenuItem key={link.path} component={RouterLink} to={link.path} onClick={closeLegalMenu}>
                            {!open && helpURL ? <ListItemText>{link.label}</ListItemText> : <span>{link.label}</span>}
                        </MuiMenuItem>
                    ))}
                    <Divider />
                    {/* wrapped in a MenuItem so the <ul role="menu"> keeps valid li children
                        and arrow-key navigation can reach the row; the row itself is not an
                        action — the inner DE | EN links are the click targets */}
                    <MuiMenuItem
                        disableRipple
                        sx={{ justifyContent: 'center', '&:hover': { backgroundColor: 'transparent' } }}
                    >
                        <LanguageLinks />
                    </MuiMenuItem>
                </Menu>
            </Box>
        </Drawer>
    );
};

export default SideDrawer;
